// src/components/pageContent/components/PostHeader.tsx
"use client";

import React from "react";
import Image from "next/image";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";
import type { WPPageOrPost } from "@/types/wpBase";
import type { ACFBuilder, ImageArray } from "@/types/acf";
import Section from "@/components/ui/Section";
import Wrapper from "@/components/ui/Wrapper";

type GlobalText = {
  posts?: {
    get_inspired_eyebrow?: string;
  };
};

type Props = {
  data: WPPageOrPost;
  blocks?: ACFBuilder[];
};

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getString(obj: unknown, key: string): string | undefined {
  if (!isRecord(obj)) return undefined;
  const v = obj[key];
  return typeof v === "string" ? v : undefined;
}

function formatDate(date?: string, locale = "en-GB") {
  const d = new Date(date ?? "");
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function localeFromLang(lang?: string) {
  if (lang === "pt") return "pt-PT";
  if (lang === "es") return "es-ES";
  return "en-GB";
}

function getFeaturedImage(data: WPPageOrPost, blocks?: ACFBuilder[]) {
  const wpFeatured = getString(data as unknown, "featured_image");
  if (typeof wpFeatured === "string" && wpFeatured.trim()) {
    return { url: wpFeatured.trim() };
  }

  if (Array.isArray(blocks)) {
    for (const b of blocks) {
      if (b.acf_fc_layout === "post_image") {
        const maybeBlock = b as unknown;

        const postImage = isRecord(maybeBlock) ? maybeBlock["post_image"] : undefined;
        const postImageRecord = isRecord(postImage) ? postImage : undefined;

        const img = (postImageRecord?.["image"] as ImageArray | undefined) ?? undefined;

        if (img?.url) return { url: img.url, alt: img.alt, width: img.width, height: img.height };
      }
    }
  }

  return null;
}

export default function PostHeader({ data, blocks }: Props) {
  const { global_text } = useLayoutData() as { global_text?: GlobalText };

  const title = data?.title?.rendered ?? "";
  const eyebrow = global_text?.posts?.get_inspired_eyebrow ?? "";

  const lang = getString(data as unknown, "lang");
  const date = getString(data as unknown, "date");

  const locale = localeFromLang(lang);
  const formatted = formatDate(date, locale);

  const featured = getFeaturedImage(data, blocks);
  const imageUrl = featured?.url ? toAbsoluteUrl(featured.url) : "";
  const alt = featured?.alt ?? "";

  return (
    <header className="w-full">
      <Section div>
        <Wrapper>
          <div className="relative w-full overflow-hidden">
            <div className="relative w-full aspect-[16/9] sm:aspect-[21/9]">
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={alt}
                  fill
                  priority
                  className="object-cover"
                  sizes="100vw"
                />
              ) : (
                <div className="absolute inset-0 bg-fuji-grey" />
              )}

              <div className="absolute inset-0 bg-black/50" />

              <div className="absolute inset-0 flex items-end">
                <div className="mx-auto w-full px-4 sm:px-6 lg:px-8 pb-10 sm:pb-14">
                  {eyebrow ? (
                    <p className="mb-3 text-title-2 font-bold text-fuji-green">{eyebrow}</p>
                  ) : null}

                  {title ? (
                    <h1 className="text-white text-title-1 max-w-[800px]">{title}</h1>
                  ) : null}

                  {formatted ? <p className="text-white mt-3 text-title-3">{formatted}</p> : null}
                </div>
              </div>
            </div>
          </div>
        </Wrapper>
      </Section>
    </header>
  );
}
